import { Alert, Box, LinearProgress, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import CustomDatePicker, { DateObject } from "react-multi-date-picker";

import { skipToken } from "@reduxjs/toolkit/query/react";
import StockSymbolAutocomplete, { SearchResult } from "components/holdings/StockSymbolAutocomplete";
import { FDS_SubscriptionTypes } from "constants/index";
import { useAppDispatch, useAuth, useFactsetStreaming } from "features/store";
import { QuoteHistoryRow, useGetSecurityQuotesQuery } from "features/trading/accounts";
import { useFDSWebSocket } from "hooks/useFDSWebSocket";
import {
  COLUMN_LABELS,
  defaultRange,
  formatPrice,
  formatQuoteValue,
  toRangeParams,
} from "./quoteHistory";
import { fdsSubKey, resolvePrice } from "./securityPrices";

const DEFAULT_SORT: GridSortModel = [{ field: "date", sort: "desc" }];

const SOURCE_LABELS = {
  factset: "Daily prices from FactSet",
  positions: "Daily closing prices from the Orion position import",
};

export default function Quotes() {
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();

  const [security, setSecurity] = useState<SearchResult | null>(null);
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => defaultRange());
  const [sorting, setSorting] = useState<GridSortModel>(DEFAULT_SORT);

  const {
    data,
    currentData: quotes,
    isFetching,
    isError,
  } = useGetSecurityQuotesQuery(
    isAuthenticated && security ? { id: security.id, ...toRangeParams(range) } : skipToken,
  );
  // The quote panel's name and stored price: `data` outlives a change of
  // range, so the panel does not blank while the table reloads — but only
  // while it is still this security's.
  const details = data && security && data.security.id === security.id ? data.security : undefined;

  // The live quote: the FDS gateway's in-memory last price, falling back to
  // the price stored from the Orion import.
  const kind = security?.kind ?? "stock";
  const { subscriptions, dataCache } = useFactsetStreaming();
  const subKey = useMemo(
    () => (security ? fdsSubKey(kind, [security]) : null),
    [kind, security],
  );
  useFDSWebSocket(
    kind === "stock" ? subKey : null,
    kind === "option" ? subKey : null,
    subscriptions,
    undefined,
    dispatch,
  );
  const fdsCache =
    dataCache[
      kind === "option" ? FDS_SubscriptionTypes.HOMEPAGE_OPTION : FDS_SubscriptionTypes.HOMEPAGE_STOCK
    ];
  const price = security
    ? resolvePrice({ symbol: security.symbol, last_price: details?.last_price ?? "" }, fdsCache)
    : undefined;
  const hasPrice = price && !(price.source === "db" && !price.value);

  const quoteColumns = useMemo(() => quotes?.columns ?? ["close" as const], [quotes?.columns]);
  const columns = useMemo<GridColDef<QuoteHistoryRow>[]>(
    () => [
      {
        field: "date",
        headerName: "Date",
        width: 130,
        valueFormatter: ({ value }) => dayjs(value).format("MM/DD/YYYY"),
      },
      ...quoteColumns.map(
        (column): GridColDef<QuoteHistoryRow> => ({
          field: column,
          headerName: COLUMN_LABELS[column],
          type: "number",
          // A lone Close column sits beside the date rather than across the page.
          ...(quoteColumns.length > 1 ? { flex: 1 } : { width: 160 }),
          minWidth: column === "volume" ? 130 : 110,
          valueFormatter: ({ value }) => formatQuoteValue(column, value),
        }),
      ),
    ],
    [quoteColumns],
  );

  const onRangeChange = (dates: DateObject[] | DateObject | null) => {
    // A range pick fires once with the start alone; the table waits for both.
    if (Array.isArray(dates) && dates.length === 2) {
      setRange([dayjs(dates[0].toDate()).startOf("day"), dayjs(dates[1].toDate()).startOf("day")]);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Box sx={{ width: { xs: "100%", sm: 300 }, flexShrink: 0 }}>
          <StockSymbolAutocomplete
            includeOptions
            label="Ticker"
            onSecurityChange={setSecurity}
          />
        </Box>
        {security && (
          <Paper
            variant="outlined"
            sx={{ px: 2, py: 1, minWidth: 0, flex: 1 }}
            aria-label="Real-time quote"
          >
            <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" component="span" sx={{ whiteSpace: "pre" }}>
                {security.symbol}
              </Typography>
              <Typography variant="h6" component="span" fontWeight={600}>
                {hasPrice ? (
                  <Tooltip
                    title={
                      price!.source === "fds"
                        ? "Last price from the FactSet feed"
                        : "Last price from the Orion import"
                    }
                  >
                    <span>{formatPrice(price!.value)}</span>
                  </Tooltip>
                ) : details || isError ? (
                  "—"
                ) : (
                  ""
                )}
              </Typography>
              {(details || hasPrice) && (
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                  {details?.name}
                  {hasPrice && (price!.source === "fds" ? " · live (FactSet)" : " · last stored price (Orion)")}
                </Typography>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Period
        </Typography>
        <CustomDatePicker
          range
          format="MM/DD/YYYY"
          dateSeparator=" - "
          value={[range[0].toDate(), range[1].toDate()]}
          maxDate={new Date()}
          onChange={onRangeChange}
          style={{ height: "40px", width: "220px", fontSize: "16px" }}
          showOtherDays
        />
        {quotes && (
          <Typography variant="caption" color="text.secondary">
            {SOURCE_LABELS[quotes.source]}
          </Typography>
        )}
      </Stack>

      {isError && <Alert severity="error">The quote history could not be loaded.</Alert>}
      {quotes && !quotes.available && (
        <Alert severity="warning">{quotes.message ?? "Quote history is unavailable."}</Alert>
      )}

      <DataGrid
        autoHeight
        density="compact"
        getRowId={(row) => row.date}
        rows={quotes?.results ?? []}
        columns={columns}
        loading={isFetching}
        slots={{ loadingOverlay: LinearProgress }}
        sortModel={sorting}
        onSortModelChange={setSorting}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50, 100]}
        disableColumnFilter
        disableRowSelectionOnClick
        localeText={{
          noRowsLabel: security
            ? "No quotes in this period"
            : "Search for a ticker to see its quotes",
        }}
      />
    </Stack>
  );
}
